import { useState, useEffect } from "react"
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  Alert,
  Snackbar,
  DialogActions,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material"
import { useNavigate } from "react-router-dom"
import { useForm, Controller, FormProvider } from "react-hook-form"

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material"
import axios from "axios"
import { fetchToken } from "../../../../Auth"
import { adminURL } from "../../../../global"

export default function NewsLetter({ privileges }) {
  const { createPrivilege, deletePrivilege, editPrivilege, livePrivilege } = privileges

  // Initialize React Hook Form
  const formMethods = useForm()
  const { control, handleSubmit, reset, setValue, getValues } = formMethods

  const [newsletterItems, setNewsletterItems] = useState([])
  // Store the original state of newsletter items before editing
  const [originalNewsletterItems, setOriginalNewsletterItems] = useState([])
  // Track newly created items that haven't been saved
  const [newItemId, setNewItemId] = useState(null)
  // Track the current mode: 'view', 'create', or 'edit'
  const [mode, setMode] = useState("view")
  // Track items marked for deletion
  const [dialogOpen, setDialogOpen] = useState(false)
  const [pendingChange, setPendingChange] = useState(null)
  const navigate = useNavigate()
  const [pendingDeletions, setPendingDeletions] = useState([])
  const [toastMessage, setToastMessage] = useState({
    open: false,
    message: "",
    severity: "info",
  })

  const [maxId, setMaxId] = useState(1)
  const [isEditMode, setIsEditMode] = useState(false)
  const [expandedItems, setExpandedItems] = useState({})
  const [imagePreview, setImagePreview] = useState({})
  const [originalImagePreviews, setOriginalImagePreviews] = useState({})
  const [defaultNewsletters, setDefaultNewsletters] = useState([])
  const [selectedFiles, setSelectedFiles] = useState({})
  const [selectedPdfs, setSelectedPdfs] = useState({})
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    itemId: null,
  })
  const [errors, setErrors] = useState({})
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [categories, setCategories] = useState([])
  const [categoryTypes, setCategoryTypes] = useState({})
  // Add a new state to track original category types
  const [originalCategoryTypes, setOriginalCategoryTypes] = useState({})
  const MAX_IMAGE_SIZE = 1 * 1024 * 1024 // 1MB
  const MAX_PDF_SIZE = 20 * 1024 * 1024 // 20MB
  // When entering edit mode, store the original state
  const getNewsletters = async () => {
    try {
      const response = await axios.get(`/backend/get_newsletters/0`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
        },
      })

      const newsletters = response.data || []
      setDefaultNewsletters(newsletters)
      setNewsletterItems(newsletters)

      // Initialize form values for each newsletter item
      const formValues = {}
      newsletters.forEach((item) => {
        formValues[`title-${item.id}`] = item.title || ""
        formValues[`volume-${item.id}`] = item.volume || ""
        formValues[`date-${item.id}`] = item.date || ""
        formValues[`category-${item.id}`] = item.category || ""
      })
      reset(formValues)

      // Set all items to use predefined categories by default
      const defaultCategoryTypes = {}
      newsletters.forEach((item) => {
        defaultCategoryTypes[item.id] = "predefined"
      })
      setCategoryTypes(defaultCategoryTypes)
    } catch (err) {
      console.error("Error", err)
      if (err.response?.status === 401) {
        navigate(`/niyantran-panel/${adminURL}/login`)
      }
    }
  }

  const getcategories = async () => {
    try {
      const response = await axios.get(`/backend/get_newsletter_categories`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
        },
      })
      setCategories(response.data)
    } catch (err) {
      console.error("Error", err)
      if (err.response?.status === 401) {
        navigate(`/niyantran-panel/${adminURL}/login`)
      }
    }
  }

  useEffect(() => {
    getNewsletters()
    fetchMaxId()
    getcategories()
  }, [])

  // Add a new state to track custom category values after the categoryTypes state
  const [customCategoryValues, setCustomCategoryValues] = useState({})

  useEffect(() => {
    if (isEditMode) {
      // Create a deep copy of the newsletter items to ensure nested objects are also copied
      setOriginalNewsletterItems(newsletterItems)

      // Store the original category types
      setOriginalCategoryTypes({ ...categoryTypes })

      // Store the original image previews
      setOriginalImagePreviews({ ...imagePreview })

      // Initialize form values when entering edit mode
      const formValues = {}
      newsletterItems.forEach((item) => {
        formValues[`title-${item.id}`] = item.title || ""
        formValues[`volume-${item.id}`] = item.volume || ""
        formValues[`date-${item.id}`] = item.date || ""
        formValues[`category-${item.id}`] = item.category || ""
      })
      reset(formValues)

      // Initialize custom category values for items with custom categories
      const initialCustomValues = {}
      newsletterItems.forEach((item) => {
        if (categoryTypes[item.id] === "custom") {
          initialCustomValues[item.id] = item.category
        }
      })
      setCustomCategoryValues(initialCustomValues)
    } else {
      // Clear tracking of new items when exiting edit mode
      setNewItemId(null)
      // Clear pending deletions when exiting edit mode
      setPendingDeletions([])
    }
  }, [isEditMode, reset])

  // Add a new useEffect to fetch images when newsletterData is loaded
  const fetchNewsletterImage = async (item) => {
    try {
      const itemId = item.id
      // Find the newsletter item to get its image type
      const imgId = `img-${itemId}`

      // Get the file extension from the image type
      let fileExtension = "jpg" // Default extension
      if (item && item[imgId]?.type) {
        const imageType = item[imgId].type
        if (imageType === "image/jpeg" || imageType === "image/jpg") {
          fileExtension = "jpeg"
        } else if (imageType === "image/png") {
          fileExtension = "png"
        } 
      }
      const imageID = `newsletter-${itemId.toString()}-${imgId}.${fileExtension}`
      // Updated URL format to include the file extension
      const response = await axios.get(`/backend/get_newsletter_image/${imageID}`, {
        headers: {
          Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
        },
      })
      return response.data // This should be the presigned URL
    } catch (error) {
      console.error(`Error fetching image for newsletter item ${item.id}:`, error)
      return null
    }
  }

  const fetchMaxId = async () => {
    try {
      const response = await axios.get("/backend/get_newsletter_max_id", {
        headers: {
          Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
        },
      })

      if (response.status === 200) {
        setMaxId(response.data.nextId)
      } else {
        console.error("Failed to get max ID:", response.data.msg)
      }
    } catch (error) {
      console.error("Error fetching max ID:", error)
    }
  }

  useEffect(() => {
    if (newsletterItems && newsletterItems.length > 0) {
      // Initialize image and PDF previews for all items
      const initialImagePreviews = {}

      newsletterItems.forEach((item) => {
        initialImagePreviews[item.id] = "/placeholder.svg?height=200&width=300"
      })

      setImagePreview((prev) => ({ ...prev, ...initialImagePreviews }))

      // Fetch actual images and PDFs for each newsletter item
      const fetchMedia = async () => {
        for (const item of newsletterItems) {
          const imgId = `img-${item.id}`
          if (item[imgId]?.type) {
            const imageUrl = await fetchNewsletterImage(item)
            if (imageUrl) {
              setImagePreview((prev) => ({ ...prev, [item.id]: imageUrl }))
            }
          }
        }
      }
      fetchMedia()
    }
  }, [defaultNewsletters])

  // Check if a newsletter item is complete with all required fields
  const isItemComplete = (item) => {
    const formValues = getValues()
    const imgId = `img-${item.id}`
    const pdfId = `pdf-${item.id}`

    return Boolean(
      formValues[`title-${item.id}`] &&
        formValues[`volume-${item.id}`] &&
        formValues[`date-${item.id}`] &&
        formValues[`category-${item.id}`] &&
        item[imgId]?.type &&
        item[pdfId]?.type,
    )
  }

  // Function to check if an item is editable based on the current mode
  const isItemEditable = (itemId) => {
    if (mode === "create") {
      // Only the newly created item is editable in create mode
      return newItemId === itemId
    }
    // All items are editable in edit mode
    return mode === "edit"
  }

  // Function to check if sequence controls should be enabled
  const isSequenceEnabled = () => {
    // Sequence controls are disabled in create mode
    return mode === "edit"
  }

  const handleAddNewsletterItem = async () => {
    // Set mode to 'create' when adding a new item
    setMode("create")
    setIsEditMode(true)

    try {
      // Fetch the next ID from the API
      const nextId = maxId + 1

      if (!nextId) {
        showToast("Failed to get the next ID. Please try again.", "error")
        setMode("view")
        setIsEditMode(false)
        return
      }

      const imgId = `img-${nextId}`
      const pdfId = `pdf-${nextId}`

      const newItem = {
        id: nextId,
        title: "",
        volume: "",
        date: "",
        category: "",
        isLive: false,
        sequence: -1,
        [imgId]: {
          type: null,
        },
        [pdfId]: {
          type: null,
        },
      }

      // Add the new item at the beginning of the array
      setNewsletterItems([newItem, ...newsletterItems])

      // Initialize form values for the new item
      setValue(`title-${nextId}`, "")
      setValue(`volume-${nextId}`, "")
      setValue(`date-${nextId}`, "")
      setValue(`category-${nextId}`, "")

      // Track only this new item
      setNewItemId(nextId)

      // Expand only the new item
      setExpandedItems({
        [nextId]: true,
      })
    } catch (error) {
      console.error("Error adding new newsletter item:", error)
      showToast("Failed to add new newsletter item. Please try again.", "error")
      setMode("view")
      setIsEditMode(false)
    }
  }

  // Update the handleCategoryTypeChange function to preserve custom category values
  const handleCategoryTypeChange = (itemId, value) => {
    setCategoryTypes((prev) => ({
      ...prev,
      [itemId]: value,
    }))

    // If switching to custom, initialize with empty string if not already set
    if (value === "custom" && !customCategoryValues[itemId]) {
      setCustomCategoryValues((prev) => ({
        ...prev,
        [itemId]: "",
      }))
    }

    // Clear the category value when switching between types
    setValue(`category-${itemId}`, "")

    // Clear error for this field if validation was attempted
    if (hasAttemptedSave && errors[itemId]?.category) {
      setErrors((prev) => ({
        ...prev,
        [itemId]: { ...(prev[itemId] || {}), category: undefined },
      }))
    }
  }

  // Add a function to handle custom category changes
  const handleCustomCategoryChange = (itemId, value) => {
    // Update the custom category value
    const trimmedValue = value.trim()
    setCustomCategoryValues((prev) => ({
      ...prev,
      [itemId]: trimmedValue,
    }))

    // Clear error for this field if validation was attempted
    if (hasAttemptedSave && errors[itemId]?.category) {
      setErrors((prev) => ({
        ...prev,
        [itemId]: { ...(prev[itemId] || {}), category: undefined },
      }))
    }
  }

  // Handle live status change directly from accordion summary
  const handleLiveStatusChange = async (itemId, isLive, e) => {
    e.stopPropagation()

    try {
      const response = await axios.post(
        "/backend/change_newsletter_live",
        {
          id: itemId,
          flag: isLive ? 1 : 0,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
          },
        },
      )

      if (response.status === 200) {
        showToast(`Newsletter ${isLive ? "published" : "unpublished"} successfully!`, "success")
        resetState()
      }
    } catch (error) {
      console.error("Error updating newsletter live status:", error)
      showToast(error.response?.data?.msg || "Failed to update newsletter live status", "error")
    }
  }

  // Update handleImageChange to store the file instead of uploading immediately
  const handleImageChange = async (itemId, e) => {
    const file = e.target.files[0]
    if (file) {
    const validImageTypes = ["image/jpeg", "image/jpg", "image/png" , "image/webp"]
    
    if (!validImageTypes.includes(file.type)) {
      showToast(`Invalid file format. Please upload only JPG, PNG,  or WebP images.`, "error")
      // Clear the file input
      e.target.value = ""
      return
    }
      if (file.size > MAX_IMAGE_SIZE) {
        showToast(`Image size exceeds the maximum limit of 1MB. Please select a smaller image.`, "error")
        // Clear the file input
        e.target.value = ""
        return
      }
      
      const imgId = `img-${itemId}`

      // Store the file for later upload
      setSelectedFiles((prev) => ({
        ...prev,
        [itemId]: file,
      }))

      // Update the newsletterItems to include the image data
      setNewsletterItems((prevNewsletterItems) =>
        prevNewsletterItems.map((item) =>
          item.id === itemId
            ? {
                ...item,
                [imgId]: { type: file.type },
              }
            : item,
        ),
      )

      // Create preview
      const reader = new FileReader()
      reader.onload = (event) => {
        setImagePreview({ ...imagePreview, [itemId]: event.target.result })
      }
      reader.readAsDataURL(file)

      // Clear error for image if validation was attempted
      if (hasAttemptedSave && errors[itemId]?.image) {
        setErrors((prev) => ({
          ...prev,
          [itemId]: { ...(prev[itemId] || {}), image: undefined },
        }))
      }
    }
  }

  // Handle PDF file change
  const handlePdfChange = async (itemId, e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.type !== "application/pdf") {
        showToast(`Invalid file format. Please upload only PDF files.`, "error")
        // Clear the file input
        e.target.value = ""
        return
      }
      if (file.size > MAX_PDF_SIZE) {
        showToast(`PDF size exceeds the maximum limit of 20MB. Please select a smaller PDF.`, "error")
        // Clear the file input
        e.target.value = ""
        return
      }
      const pdfId = `pdf-${itemId}`

      // Store the file for later upload
      setSelectedPdfs((prev) => ({
        ...prev,
        [itemId]: file,
      }))

      // Update the newsletterItems to include the PDF data
      setNewsletterItems((prevNewsletterItems) =>
        prevNewsletterItems.map((item) =>
          item.id === itemId
            ? {
                ...item,
                [pdfId]: { type: file.type, name: file.name },
              }
            : item,
        ),
      )

      // Clear error for PDF if validation was attempted
      if (hasAttemptedSave && errors[itemId]?.pdf) {
        setErrors((prev) => ({
          ...prev,
          [itemId]: { ...(prev[itemId] || {}), pdf: undefined },
        }))
      }
    }
  }

  const handleMoveItem = (index, direction) => {
    // Only allow sequence changes in edit mode
    if (!isSequenceEnabled()) return

    const newItems = [...newsletterItems]
    const item = newItems.splice(index, 1)[0]
    newItems.splice(index + direction, 0, item)

    // Update sequences only for live items
    const updatedItems = newItems.map((item, idx) => ({
      ...item,
      sequence: item.isLive
        ? // Find all live items and assign sequential numbers
          newItems.filter((i) => i.isLive).findIndex((i) => i.id === item.id) + 1
        : -1,
    }))

    setNewsletterItems(updatedItems)
  }

  // Modified to mark item for deletion instead of deleting immediately
  const handleMarkItemForDeletion = (itemId) => {
    // For newly created items that haven't been saved yet, just remove them
    if (newItemId === itemId) {
      setNewsletterItems((prevNewsletterItems) => prevNewsletterItems.filter((item) => item.id !== itemId))

      // Clean up the image and PDF previews
      const newImagePreview = { ...imagePreview }
      delete newImagePreview[itemId]
      setImagePreview(newImagePreview)
    } else {
      // Add existing items to the pending deletions list
      setPendingDeletions((prev) => [...prev, itemId])
    }

    setDeleteDialog({ open: false, itemId: null })
  }

  // Add this function to handle API deletion - now only called during save
  const handleDeleteNewsletters = async (itemIds) => {
    try {
      // Prepare the payload as required by the backend
      const payload = {
        ids: itemIds,
      }

      // Call the delete API
      const response = await axios.post("/backend/delete_newsletters", payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
        },
      })

      // Return the result so the save function can track successful deletions
      return response.data
    } catch (error) {
      console.error(`Error deleting newsletter items:`, error)
      showToast(error.response?.data?.msg || `Failed to delete newsletter items`, "error")
      throw new Error(error.response?.data?.msg || `Failed to delete newsletter items`)
    }
  }

  // Update the handleDeleteConfirm function to mark for deletion instead of immediate deletion
  const handleDeleteConfirm = () => {
    handleMarkItemForDeletion(deleteDialog.itemId)
  }

  const handleEdit = () => {
    if (editPrivilege) {
      setMode("edit")
      setIsEditMode(true)
      // Store the original state when entering edit mode
      setOriginalNewsletterItems([...newsletterItems])

      // Initialize form values for all items
      const formValues = {}
      newsletterItems.forEach((item) => {
        formValues[`title-${item.id}`] = item.title || ""
        formValues[`volume-${item.id}`] = item.volume || ""
        formValues[`date-${item.id}`] = item.date || ""
        formValues[`category-${item.id}`] = item.category || ""
      })
      reset(formValues)

      // Clear any pending deletions
      setPendingDeletions([])
    }
  }

  // Custom function to handle accordion expansion/collapse
  const handleAccordionChange = (itemId) => {
    // In create mode, if trying to collapse the new item, check if it's complete
    if (mode === "create" && newItemId === itemId && expandedItems[itemId]) {
      const item = newsletterItems.find((item) => item.id === itemId)

      // If the item is not complete, prevent collapsing
      if (item && !isItemComplete(item)) {
        showToast("Please complete all required fields before collapsing this item.", "warning")
        return
      }
    }

    // Toggle the expanded state
    toggleExpand(itemId)
  }

  const resetState = async () => {
    setMode("view")
    setIsEditMode(false)
    setHasAttemptedSave(false)
    setNewItemId(null)
    setSelectedFiles({})
    setSelectedPdfs({})
    setPendingDeletions([])
    setExpandedItems({})
    setNewsletterItems([])
    setDefaultNewsletters([])
    setCategories([])
    // Reset custom category values and category types
    setCustomCategoryValues({})
    setCategoryTypes({})

    // Reset form
    reset({})

    fetchMaxId()
    getNewsletters()
    getcategories()
  }

  // Add this function after the resetState function to handle toast messages
  const displayToast = (message, severity = "info") => {
    setToastMessage({
      open: true,
      message,
      severity,
    })
  }

  // Update handleSave to process pending deletions and toggle accordion state
  const onSubmit = async (formData) => {
    setHasAttemptedSave(true)
    const validationErrors = {}
    let isValid = true

    // In create mode, only validate the new item
    const itemsToValidate =
      mode === "create"
        ? newsletterItems.filter((item) => newItemId === item.id)
        : newsletterItems.filter((item) => !pendingDeletions.includes(item.id))

    itemsToValidate.forEach((item) => {
      const itemErrors = {}
      const imgId = `img-${item.id}`
      const pdfId = `pdf-${item.id}`
      const trimmedFormData = { ...formData };
      const titleKey = `title-${item.id}`
      const volumeKey = `volume-${item.id}`
    const categoryKey = `category-${item.id}`
      // Trim the values and update the trimmedFormData
      if (formData[titleKey]) {
        trimmedFormData[titleKey] = formData[titleKey].trim()
        // Also update the form value with trimmed value
        setValue(titleKey, trimmedFormData[titleKey])
      }
    
      if (formData[volumeKey]) {
        trimmedFormData[volumeKey] = formData[volumeKey].trim()
        // Also update the form value with trimmed value
        setValue(volumeKey, trimmedFormData[volumeKey])
      }
      if (formData[categoryKey] && categoryTypes[item.id] === "custom") {
        trimmedFormData[categoryKey] = formData[categoryKey].trim()
        // Update the form value with trimmed value
        setValue(categoryKey, trimmedFormData[categoryKey])
      }
    
      // Title validation with trimmed value
      const title = trimmedFormData[titleKey]
    if (!title) {
      itemErrors.title = "Title is required"
      isValid = false
    } else if (title.length > 50) {
      itemErrors.title = "Title must be 50 characters or less"
      isValid = false
    } else if (/[<>%$]/.test(title)) {
      itemErrors.title = "Title contains invalid characters"
      isValid = false
    } else if (/^\d+$/.test(title)) {
      itemErrors.title = "Title cannot contain only numbers"
      isValid = false
    }

    // Volume validation
    const volume = trimmedFormData[volumeKey]
    if (!volume) {
      itemErrors.volume = "Volume is required"
      isValid = false
    } else if (volume.length > 20) {
      itemErrors.volume = "Volume must be 20 characters or less"
      isValid = false
    } else if (!/^[a-zA-Z0-9\s.-]+$/.test(volume)) {
      itemErrors.volume = "Volume can only contain letters, numbers, spaces, dots and hyphens"
      isValid = false
    }
    
      if (!formData[`date-${item.id}`]) {
        itemErrors.date = "Date is required"
        isValid = false
      }
      const categoryValue = trimmedFormData[`category-${item.id}`]
      if (!categoryValue) {
        itemErrors.category = "Category is required"
        isValid = false
      }else if (/^\d+$/.test(categoryValue)) {
        // Check if category contains only numeric characters
        itemErrors.category = "Category cannot contain only numbers"
        isValid = false
      }else if (/[^a-zA-Z0-9\s]/.test(categoryValue)) {
        // Check if category contains any special characters
        itemErrors.category = "Category cannot contain special characters"
        isValid = false
      }
      if (!item[imgId]?.type) {
        itemErrors.image = "Image is required"
        isValid = false
      }
      if (!item[pdfId]?.type) {
        itemErrors.pdf = "PDF is required"
        isValid = false
      }

      if (Object.keys(itemErrors).length > 0) {
        validationErrors[item.id] = itemErrors
      }
    })

    setErrors(validationErrors)

    if (!isValid) {
      // Prepare toast message for incomplete fields
      const incompleteItems = Object.keys(validationErrors).map((itemId) => {
        const item = newsletterItems.find((item) => item.id.toString() === itemId)
        const itemTitle = item && formData[`title-${item.id}`] ? formData[`title-${item.id}`] : ``
        const missingFields = Object.keys(validationErrors[itemId]).join(", ")
        return `${itemTitle}: Missing or invalid ${missingFields}`
      })

      // Show toast with all incomplete items
      const toastMessage = incompleteItems.join("; ")
      displayToast(toastMessage, "error")
      return
    }

    setIsSaving(true)

    try {
      // Process pending deletions first
      if (pendingDeletions.length > 0) {
        try {
          await handleDeleteNewsletters(pendingDeletions)
        } catch (error) {
          console.error(`Failed to delete items:`, error)
          showToast(`Failed to delete items: ${error.message}`, "error")
          throw new Error(`Failed to delete items: ${error.message}`)
        }
      }

      // Update items with form values
      const updatedItems = newsletterItems.map((item) => ({
        ...item,
        title: formData[`title-${item.id}`] || "",
        volume: formData[`volume-${item.id}`] || "",
        date: formData[`date-${item.id}`] || "",
        category: formData[`category-${item.id}`] || "",
      }))

      // For create mode, we'll upload files after getting the ID from create_newsletter
      // For edit mode, upload any new images or PDFs for existing items
      const mediaUploadPromises =
        mode === "create"
          ? [] // Skip uploads for new items here, we'll do it after create_newsletter
          : newItemId
            ? [newItemId].map(async (itemId) => {
                const uploadPromises = []

                // Upload image if exists
                if (selectedFiles[itemId]) {
                  const formData = new FormData()
                  const imgId = `img-${itemId}`

                  const fileType = selectedFiles[itemId].type
                  const fileExtension =
                    fileType === "image/jpeg"
                      ? "jpeg"
                      : fileType === "image/jpg"
                        ? "jpg"
                        : fileType === "image/png"
                          ? "png"
                          : "jpg"
                  const imageID = `newsletter-${itemId.toString()}-${imgId}.${fileExtension}`
                  formData.append("img_id", `${imageID}`)
                  formData.append("file", selectedFiles[itemId])

                  try {
                    const response = await axios.post("/backend/upload_newsletter_images", formData, {
                      headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
                      },
                    })
                    uploadPromises.push(response.data)
                  } catch (error) {
                    console.error(`Error uploading image for item ${itemId}:`, error)
                    showToast(`Failed to upload image for item ${itemId}: ${error.message}`, "error")
                    throw new Error(`Failed to upload image for item ${itemId}: ${error.message}`)
                  }
                }

                // Upload PDF if exists
                if (selectedPdfs[itemId]) {
                  const formData = new FormData()
                  const pdfId = `pdf-${itemId}`

                  const pdfID = `newsletter-${itemId.toString()}-${pdfId}.pdf`
                  formData.append("pdf_id", `${pdfID}`)
                  formData.append("file", selectedPdfs[itemId])

                  try {
                    const response = await axios.post("/backend/upload_newsletter_pdfs", formData, {
                      headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
                      },
                    })
                    uploadPromises.push(response.data)
                  } catch (error) {
                    console.error(`Error uploading PDF for item ${itemId}:`, error)
                    showToast(`Failed to upload PDF for item ${itemId}: ${error.message}`, "error")
                    throw new Error(`Failed to upload PDF for item ${itemId}: ${error.message}`)
                  }
                }

                return Promise.all(uploadPromises)
              })
            : []

      // Wait for all media uploads to complete
      await Promise.all(mediaUploadPromises)

      // Then create or update the newsletter items based on mode
      if (mode === "create") {
        // Only create the new items
        const item = updatedItems.find((item) => item.id === newItemId)
        if (item) {
          const imgId = `img-${item.id}`
          const pdfId = `pdf-${item.id}`

          const newsletterItemData = {
            title: item.title,
            volume: item.volume,
            date: item.date,
            category: item.category,
            isLive: item.isLive,
            imgKey: item[imgId],
            pdfKey: item[pdfId],
          }

          try {
            // First call the create_newsletter API to get the ID
            const createResponse = await axios.post("/backend/create_newsletter", newsletterItemData, {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
              },
            })

            console.log(newsletterItemData, "_____________________________")
            // Get the ID from the response
            const responseId = createResponse.data.id
            // Now upload image if exists
            if (selectedFiles[item.id]) {
              const formData = new FormData()
              const imgId = `img-${responseId}` // Use responseId instead of item.id

              const fileType = selectedFiles[item.id].type
              const fileExtension =
                fileType === "image/jpeg"
                  ? "jpeg"
                  : fileType === "image/jpg"
                    ? "jpg"
                    : fileType === "image/png"
                      ? "png"
                      : "jpg"
              const imageID = `newsletter-${responseId.toString()}-${imgId}.${fileExtension}`
              formData.append("img_id", `${imageID}`)
              formData.append("file", selectedFiles[item.id])

              await axios.post("/backend/upload_newsletter_images", formData, {
                headers: {
                  "Content-Type": "multipart/form-data",
                  Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
                },
              })
            }

            // Upload PDF if exists
            if (selectedPdfs[item.id]) {
              const formData = new FormData()
              const pdfId = `pdf-${responseId}` // Use responseId instead of item.id

              const pdfID = `newsletter-${responseId.toString()}-${pdfId}.pdf`
              formData.append("pdf_id", `${pdfID}`)
              formData.append("file", selectedPdfs[item.id])

              await axios.post("/backend/upload_newsletter_pdfs", formData, {
                headers: {
                  "Content-Type": "multipart/form-data",
                  Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
                },
              })
            }
          } catch (error) {
            console.error("Error creating newsletter item:", error)
            showToast(error.response?.data?.msg || "Failed to create newsletter item", "error")
            throw new Error(error.response?.data?.msg || "Failed to create newsletter item")
          }
        }
      } else if (mode === "edit") {
        // Filter out pending deletions when handling updates
        const itemsToUpdate = updatedItems
          // .filter((item) => item.id !== newItemId)
          .filter((item) => !pendingDeletions.includes(item.id))

        // First, upload any new images or PDFs for existing items
        const mediaUploadPromises = []

        // Handle image uploads for existing items
        Object.keys(selectedFiles)
          .filter((itemId) => Number.parseInt(itemId) !== newItemId)
          .forEach((itemId) => {
            const numericId = Number.parseInt(itemId)
            if (selectedFiles[numericId]) {
              const formData = new FormData()
              const imgId = `img-${itemId}`
              const fileType = selectedFiles[numericId].type
              const fileExtension =
                fileType === "image/jpeg"
                  ? "jpeg"
                  : fileType === "image/jpg"
                    ? "jpg"
                    : fileType === "image/png"
                      ? "png"
                      : "jpg"
              const imageID = `newsletter-${itemId.toString()}-${imgId}.${fileExtension}`
              formData.append("img_id", `${imageID}`)
              formData.append("file", selectedFiles[numericId])

              const uploadPromise = axios
                .post("/backend/upload_newsletter_images", formData, {
                  headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
                  },
                })
                .catch((error) => {
                  console.error(`Error uploading image for item ${numericId}:`, error)
                  showToast(`Failed to upload image for item ${numericId}: ${error.message}`, "error")
                  throw new Error(`Failed to upload image for item ${numericId}: ${error.message}`)
                })

              mediaUploadPromises.push(uploadPromise)
            }
          })

        // Handle PDF uploads for existing items
        Object.keys(selectedPdfs)
          .filter((itemId) => Number.parseInt(itemId) !== newItemId)
          .forEach((itemId) => {
            const numericId = Number.parseInt(itemId)
            if (selectedPdfs[numericId]) {
              const formData = new FormData()
              const pdfId = `pdf-${itemId}`

              const pdfID = `newsletter-${itemId.toString()}-${pdfId}.pdf`
              formData.append("pdf_id", `${pdfID}`)
              formData.append("file", selectedPdfs[numericId])

              const uploadPromise = axios
                .post("/backend/upload_newsletter_pdfs", formData, {
                  headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
                  },
                })
                .catch((error) => {
                  console.error(`Error uploading PDF for item ${numericId}:`, error)
                  showToast(`Failed to upload PDF for item ${numericId}: ${error.message}`, "error")
                  throw new Error(`Failed to upload PDF for item ${numericId}: ${error.message}`)
                })

              mediaUploadPromises.push(uploadPromise)
            }
          })

        // Wait for all media uploads to complete
        await Promise.all(mediaUploadPromises)

        // Prepare an array of edited items to send in a single API call
        const editedItems = []

        // Process each item to check if it's been edited
        for (const item of itemsToUpdate) {
          // Find the original item to compare changes
          const originalItem = originalNewsletterItems.find((orig) => orig.id === item.id)
          const imgId = `img-${item.id}`
          const pdfId = `pdf-${item.id}`

          // Check if any field has been edited
          let isEdited = false

          if (originalItem) {
            // Compare each field individually
            const titleChanged = item.title !== originalItem.title
            const volumeChanged = item.volume !== originalItem.volume
            const dateChanged = item.date !== originalItem.date
            const categoryChanged = item.category !== originalItem.category
            const imageMetaChanged = JSON.stringify(item[imgId]) !== JSON.stringify(originalItem[imgId])
            const pdfMetaChanged = JSON.stringify(item[pdfId]) !== JSON.stringify(originalItem[pdfId])
            const hasNewImage = Boolean(selectedFiles[item.id])
            const hasNewPdf = Boolean(selectedPdfs[item.id])

            isEdited =
              titleChanged ||
              volumeChanged ||
              dateChanged ||
              categoryChanged ||
              imageMetaChanged ||
              pdfMetaChanged ||
              hasNewImage ||
              hasNewPdf
          }

          // Skip items that haven't been edited
          if (!isEdited) {
            continue
          }

          // Prepare the payload for the edited item
          const newsletterItemData = {
            id: item.id,
            title: item.title,
            volume: item.volume,
            date: item.date,
            category: item.category,
            isLive: item.isLive,
            [imgId]: item[imgId],
            [pdfId]: item[pdfId],
            isEdited: isEdited,
          }

          // Add to the array of edited items
          editedItems.push(newsletterItemData)
        }

        // If there are edited items, send them in a batch
        if (editedItems.length > 0) {
          try {
            // Call the edit_newsletter API with an array of items
            const response = await axios.post("/backend/edit_newsletter", editedItems, {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
              },
            })
          } catch (error) {
            console.error("Error updating newsletter items:", error)
            showToast(error.response?.data?.msg || `Failed to update newsletter items`, "error")
            throw new Error(error.response?.data?.msg || `Failed to update newsletter items`)
          }
        }
      }

      displayToast("Newsletter saved successfully!", "success")
      resetState()
    } catch (error) {
      console.error("Error saving newsletter items:", error)
      displayToast(`Failed to save newsletter items: ${error.message}`, "error")
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setShowCancelDialog(true)
  }

  // Update the handleCancelConfirm function to also reset custom category values
  const handleCancelConfirm = () => {
    // If we have new items that haven't been saved, remove them
    if (newItemId !== null) {
      setNewsletterItems((prevNewsletterItems) => prevNewsletterItems.filter((item) => item.id !== newItemId))

      // Clean up image and PDF previews for unsaved items
      const newImagePreview = { ...imagePreview }
      if (newItemId !== null) {
        delete newImagePreview[newItemId]
      }
      setImagePreview(newImagePreview)
    } else {
      // If no new items, revert to the original state
      setNewsletterItems(originalNewsletterItems)

      // Reset image previews to their original state
      setImagePreview(originalImagePreviews)
    }

    // Reset category types to original state
    setCategoryTypes(originalCategoryTypes)

    // Reset custom category values
    setCustomCategoryValues({})

    // Reset state
    setMode("view")
    setIsEditMode(false)
    setHasAttemptedSave(false)
    setErrors({})
    setNewItemId(null)
    setSelectedFiles({}) // Clear selected files on cancel
    setSelectedPdfs({}) // Clear selected PDFs on cancel
    setPendingDeletions([]) // Clear pending deletions on cancel
    setShowCancelDialog(false)

    // Reset form to original values
    reset()
  }

  const handleCancelDialogClose = () => {
    setShowCancelDialog(false)
  }

  const toggleExpand = (itemId) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }))
  }

  // Helper function to check if an item is pending deletion
  const isItemPendingDeletion = (itemId) => {
    return pendingDeletions.includes(itemId)
  }

  

  // Function to show toast messages
  const showToast = (message, severity = "info") => {
    setToastMessage({
      open: true,
      message,
      severity,
    })

    // Auto-hide after 6 seconds
    setTimeout(() => {
      setToastMessage((prev) => ({ ...prev, open: false }))
    }, 6000)
  }

  return (
    <FormProvider {...formMethods}>
      <Box sx={{ width: "100%", maxWidth: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box>
            <Typography variant="h4" gutterBottom>
              NewsLetters
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Manage your newsletter publications
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            {isEditMode ? (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSubmit(onSubmit)}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : mode === "create" ? "Save Newsletter" : "Save Changes"}
                </Button>
                <Button variant="outlined" color="warning" onClick={handleCancel}>
                  Cancel
                </Button>
              </>
            ) : (
              <>
                {editPrivilege && (
                  <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEdit}>
                    Edit
                  </Button>
                )}
              </>
            )}

            {/* Add Newsletter button is conditionally visible based on mode */}
            {createPrivilege && mode !== "edit" && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddNewsletterItem}
                disabled={mode === "create"}
              >
                Add Newsletter
              </Button>
            )}
          </Box>
        </Box>

        {/* Mode indicator */}
        {isEditMode && (
          <Box sx={{ mb: 3 }}>
            <Alert
              severity="info"
              icon={mode === "create" ? <AddIcon /> : <EditIcon />}
              action={
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "4px",
                    backgroundColor: mode === "create" ? "rgba(25, 118, 210, 0.12)" : "rgba(156, 39, 176, 0.12)",
                    color: mode === "create" ? "primary.main" : "secondary.main",
                    border: "1px solid",
                    borderColor: mode === "create" ? "primary.light" : "secondary.light",
                    typography: "caption",
                    fontWeight: "medium",
                  }}
                >
                  {mode === "create" ? "Create Mode" : "Edit Mode"}
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      ml: 1,
                      backgroundColor: mode === "create" ? "primary.main" : "secondary.main",
                    }}
                  />
                </Box>
              }
            >
              {mode === "create"
                ? "You are creating a new newsletter. Other items cannot be edited at this time."
                : "You are in edit mode. You can modify all newsletter items."}
            </Alert>
          </Box>
        )}

        {/* Show a summary of pending deletions if any */}
        {pendingDeletions.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Alert severity="warning">
              {pendingDeletions.length} {pendingDeletions.length === 1 ? "item" : "items"} marked for deletion. Changes
              will be applied when you click "Save Changes".
            </Alert>
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {newsletterItems.length !== 0 &&
            newsletterItems.map((item, index) => {
              const isPendingDeletion = isItemPendingDeletion(item.id)
              const isNewItem = newItemId === item.id
              const isGreyedOut = mode === "create" && !isNewItem
              const pdfId = `pdf-${item.id}`

              return (
                <Accordion
                  key={item.id}
                  expanded={Boolean(expandedItems[item.id])}
                  onChange={() => handleAccordionChange(item.id)}
                  sx={{
                    mb: 2,
                    backgroundColor: isPendingDeletion
                      ? "rgba(255, 0, 0, 0.05)"
                      : isGreyedOut
                        ? "rgba(0, 0, 0, 0.05)"
                        : "white",
                    boxShadow: 1,
                    borderRadius: 2,
                    opacity: isPendingDeletion || isGreyedOut ? 0.7 : 1,
                    position: "relative",
                  }}
                  disabled={isGreyedOut}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      "& .MuiAccordionSummary-content": {
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flex: 1,
                      }}
                    >
                      <Typography variant="h6">{item.title || "New Newsletter"} </Typography>

                      {/* Live Status in summary */}
                      {livePrivilege && mode !== "create" && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            ml: "auto",
                            mr: 2,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Status:
                          </Typography>
                          <Switch
                            size="small"
                            checked={Boolean(item.isLive)}
                            onChange={(e) => {
                              e.stopPropagation()
                              setPendingChange({
                                itemId: item.id,
                                isLive: e.target.checked,
                                event: e,
                              })
                              setDialogOpen(true)
                            }}
                            disabled={!livePrivilege || isPendingDeletion || mode === "edit" || mode === "create"}
                            color="primary"
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70 }}>
                            {item.isLive ? "Published" : "Draft"}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ display: "flex", gap: 1 }}>
                      {mode !== "create" && (
                        <IconButton
                          color={isPendingDeletion ? "default" : "error"}
                          onClick={(e) => {
                            e.stopPropagation()
                            if (!isPendingDeletion) {
                              setDeleteDialog({ open: true, itemId: item.id })
                            }
                          }}
                          disabled={!isEditMode || !deletePrivilege || isPendingDeletion || isGreyedOut}
                          sx={{ display: deletePrivilege ? "flex" : "none" }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      <Controller
                        name={`title-${item.id}`}
                        control={control}
                        defaultValue={item.title || ""}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            required
                            label="Title"
                            disabled={!isEditMode || !isItemEditable(item.id) || isPendingDeletion}
                            error={hasAttemptedSave && Boolean(errors[item.id]?.title)}
                            helperText={hasAttemptedSave && errors[item.id]?.title}
                          />
                        )}
                      />

                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Controller
                          name={`volume-${item.id}`}
                          control={control}
                          defaultValue={item.volume || ""}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              sx={{ flex: 1 , maxWidth: '420px'}}
                              required
                              label="Volume"
                              disabled={!isEditMode || !isItemEditable(item.id) || isPendingDeletion}
                              error={hasAttemptedSave && Boolean(errors[item.id]?.volume)}
                              helperText={hasAttemptedSave && errors[item.id]?.volume}
                              placeholder="Vol 1"
                            />
                          )}
                        />

                        <Controller
                          name={`date-${item.id}`}
                          control={control}
                          defaultValue={item.date || ""}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              sx={{ flex: 1,  maxWidth: '220px' }}
                              required
                              label="Date"
                              type="date"
                              disabled={!isEditMode || !isItemEditable(item.id) || isPendingDeletion}
                              InputLabelProps={{ shrink: true }}
                              error={hasAttemptedSave && Boolean(errors[item.id]?.date)}
                              helperText={hasAttemptedSave && errors[item.id]?.date}
                            />
                          )}
                        />
                      </Box>

                      {/* Category selection row - moved below date and volume */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 , mb:1 }}>
                        {/* Radio buttons for category type selection - now at the start */}
                        <RadioGroup
                          row
                          value={categoryTypes[item.id] || "predefined"}
                          onChange={(e) => handleCategoryTypeChange(item.id, e.target.value)}
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <FormControlLabel
                            value="predefined"
                            control={<Radio />}
                            label="Category"
                            disabled={!isEditMode || !isItemEditable(item.id) || isPendingDeletion}
                          />
                          <FormControlLabel
                            value="custom"
                            control={<Radio />}
                            label="Custom"
                            disabled={!isEditMode || !isItemEditable(item.id) || isPendingDeletion}
                          />
                        </RadioGroup>

                        {/* Category field - now after radio buttons */}
                        <Box sx={{ flex: 1 }}>
                          {(categoryTypes[item.id] || "predefined") === "predefined" ? (
                            <FormControl fullWidth sx={{ maxWidth: '220px' }} error={hasAttemptedSave && Boolean(errors[item.id]?.category)} >
                              <InputLabel id={`category-label-${item.id}`}>Select Category</InputLabel>
                              <Controller
                                name={`category-${item.id}`}
                                control={control}
                                defaultValue={item.category || ""}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    labelId={`category-label-${item.id}`}
                                    label="Select Category"
                                    disabled={!isEditMode || !isItemEditable(item.id) || isPendingDeletion}
                                  >
                                    {categories.map((category) => (
                                      <MenuItem key={category} value={category}>
                                        {category}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                )}
                              />
                              {hasAttemptedSave && errors[item.id]?.category && (
                                <FormHelperText>{errors[item.id].category}</FormHelperText>
                              )}
                            </FormControl>
                          ) : (
                            <Controller
                              name={`category-${item.id}`}
                              control={control}
                              defaultValue={customCategoryValues[item.id] || ""}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  sx={{ maxWidth: '220px' }}
                                  required
                                  label="Custom Category"
                                  disabled={!isEditMode || !isItemEditable(item.id) || isPendingDeletion}
                                  error={hasAttemptedSave && !field.value}
                                  helperText={hasAttemptedSave && !field.value ? "Custom category is required" : ""}
                                />
                              )}
                            />
                          )}
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Button
                            variant="outlined"
                            component="label"
                            startIcon={<ImageIcon />}
                            disabled={!isEditMode || !isItemEditable(item.id) || isPendingDeletion}
                          >
                            Upload Cover Image
                            <Typography component="span" color="error" sx={{ ml: 0.5 }}>
                              *
                            </Typography>
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={(e) => handleImageChange(item.id, e)}
                              disabled={!isEditMode || !isItemEditable(item.id) || isPendingDeletion}
                            />
                          </Button>
                          {imagePreview[item.id] && (
                            <Typography variant="body2" color="text.secondary">
                              Image selected
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Maximum image size: 1MB
                        </Typography>
                        {hasAttemptedSave && errors[item.id]?.image && (
                          <Typography color="error" variant="caption">
                            {errors[item.id].image}
                          </Typography>
                        )}
                        {imagePreview[item.id] && (
                          <Box
                            sx={{
                              width: "100%",
                              maxWidth: "300px",
                              height: "200px",
                              position: "relative",
                              overflow: "hidden",
                              borderRadius: 1,
                            }}
                          >
                            <img
                              src={imagePreview[item.id] || "/placeholder.svg"}
                              alt="Preview"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                opacity: isPendingDeletion ? 0.5 : 1,
                              }}
                            />
                          </Box>
                        )}

                        {/* PDF Upload Section */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 2,
                          }}
                        >
                          <Button
                            variant="outlined"
                            component="label"
                            startIcon={<PdfIcon />}
                            disabled={!isEditMode || !isItemEditable(item.id) || isPendingDeletion}
                          >
                            Upload PDF
                            <Typography component="span" color="error" sx={{ ml: 0.5 }}>
                              *
                            </Typography>
                            <input
                              type="file"
                              hidden
                              accept="application/pdf"
                              onChange={(e) => handlePdfChange(item.id, e)}
                              disabled={!isEditMode || !isItemEditable(item.id) || isPendingDeletion}
                            />
                          </Button>
                          {item[pdfId]?.name && (
                            <Typography variant="body2" color="text.secondary">
                              {item[pdfId].name}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Maximum file size: 20MB
                        </Typography>
                        {hasAttemptedSave && errors[item.id]?.pdf && (
                          <Typography color="error" variant="caption">
                            {errors[item.id].pdf}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )
            })}
        </Box>

        {/* Delete Dialog */}
        <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, itemId: null })}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>
              {mode === "edit"
                ? "This item will be marked for deletion and will be deleted when you save changes. Proceed?"
                : "Are you sure you want to delete this newsletter?"}
            </Typography>
          </DialogContent>
          <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2 }}>
            <Button onClick={() => setDeleteDialog({ open: false, itemId: null })}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" disabled={isSaving}>
              {mode === "edit" ? "Mark for Deletion" : "Delete"}
            </Button>
          </Box>
        </Dialog>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} aria-labelledby="live-status-dialog-title">
          <DialogTitle id="live-status-dialog-title">Confirm Live Status Change</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to {pendingChange?.isLive ? "publish" : "unpublish"} this newsletter?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} color="primary">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (pendingChange) {
                  handleLiveStatusChange(pendingChange.itemId, pendingChange.isLive, pendingChange.event)
                }
                setDialogOpen(false)
                setPendingChange(null)
              }}
              color="primary"
              variant="contained"
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Dialog */}
        <Dialog open={showCancelDialog} onClose={handleCancelDialogClose}>
          <DialogTitle>Cancel Changes</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to cancel your changes? Unsaved changes will be lost.</Typography>
            {pendingDeletions.length > 0 && (
              <Typography sx={{ mt: 1, color: "warning.main" }}>
                {pendingDeletions.length} {pendingDeletions.length === 1 ? "item" : "items"} marked for deletion will be
                restored.
              </Typography>
            )}
          </DialogContent>
          <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2 }}>
            <Button onClick={handleCancelDialogClose}>Cancel</Button>
            <Button onClick={handleCancelConfirm}>Confirm</Button>
          </Box>
        </Dialog>

        {/* Toast Message */}
        <Snackbar
          open={toastMessage.open}
          autoHideDuration={6000}
          onClose={() => setToastMessage((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setToastMessage((prev) => ({ ...prev, open: false }))}
            severity={toastMessage.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {toastMessage.message}
          </Alert>
        </Snackbar>
      </Box>
    </FormProvider>
  )
}
